/**
 * Encounter (lần khám) types. Doctor's primary working surface.
 *
 * <p>Status machine:
 * IN_PROGRESS → SIGNED (terminal — clinical notes locked + PDF generated)
 *             → CANCELLED (admin reverse, rare; audited)
 */

export type EncounterStatus = "IN_PROGRESS" | "SIGNED" | "CANCELLED";

export interface Vitals {
  systolicBp: number | null;
  diastolicBp: number | null;
  heartRate: number | null;
  temperatureC: number | null;
  respiratoryRate: number | null;
  spO2: number | null;
  weightKg: number | null;
  heightCm: number | null;
  recordedAt: string | null;
}

/**
 * Structured outpatient examination note — the real Vietnamese form, not a
 * generic 4-box SOAP. Mirrors the backend {@code SoapResponse} 1:1
 * (content_schema_version = v2). Legacy v1 rows are folded into these fields
 * by the backend reader so old encounters still open.
 */
export interface SoapHistory {
  /** Lý do đi khám (also denormalised onto the encounter chief complaint). */
  chiefComplaint: string;
  /** Bệnh sử / quá trình bệnh lý (TCCN). */
  presentIllness: string;
  personalHistory: string;
  familyHistory: string;
}

/** Khám theo cơ quan — 9 hệ. */
export interface SoapExam {
  status: string;
  general: string;
  skin: string;
  headNeck: string;
  cardio: string;
  resp: string;
  abdomen: string;
  musculoskeletal: string;
  neuro: string;
}

/** Đánh giá hai giai đoạn: sơ bộ → xác định, kèm 2 trường kết quả CLS. */
export interface SoapAssessment {
  priorClsResults: string;
  preliminaryProblem: string;
  preliminaryPlan: string;
  supplementaryLabUs: string;
  definitiveDiagnosis: string;
  definitivePlan: string;
}

export interface SoapNote {
  history: SoapHistory;
  exam: SoapExam;
  assessment: SoapAssessment;
  updatedAt: string;
}

/** Vietnamese default sentences a fresh note shows (mirrors backend NoteContent). */
export const SOAP_DEFAULTS = {
  priorClsResults: "Chưa có dữ kiện cận lâm sàng nổi bật cần lưu ý",
  supplementaryLabUs: "Chưa cần bổ sung thêm cận lâm sàng và siêu âm",
  unchanged: "Không thay đổi so với kết quả sơ bộ",
} as const;

/** Empty v2 note with the same VN defaults the backend applies. */
export function emptySoapNote(updatedAt = new Date().toISOString()): SoapNote {
  return {
    history: {
      chiefComplaint: "",
      presentIllness: "",
      personalHistory: "",
      familyHistory: "",
    },
    exam: {
      status: "",
      general: "",
      skin: "",
      headNeck: "",
      cardio: "",
      resp: "",
      abdomen: "",
      musculoskeletal: "",
      neuro: "",
    },
    assessment: {
      priorClsResults: SOAP_DEFAULTS.priorClsResults,
      preliminaryProblem: "",
      preliminaryPlan: "",
      supplementaryLabUs: SOAP_DEFAULTS.supplementaryLabUs,
      definitiveDiagnosis: SOAP_DEFAULTS.unchanged,
      definitivePlan: SOAP_DEFAULTS.unchanged,
    },
    updatedAt,
  };
}

export interface Diagnosis {
  id: string;
  icd10Code: string | null;
  label: string;
  primary: boolean;
  note: string | null;
}

export interface EncounterPrescriptionItem {
  id: string;
  medicineId: string;
  medicineName: string;
  medicineUnit: string;
  medicineStrength: string | null;
  quantity: number;
  doseMorning: number;
  doseNoon: number;
  doseAfternoon: number;
  doseEvening: number;
  doseUnit: string;
  route: string | null;
  mealTiming:
    | "BEFORE_MEAL"
    | "WITH_MEAL"
    | "AFTER_MEAL"
    | "EMPTY_STOMACH"
    | "BEDTIME"
    | "ANYTIME"
    | null;
  note: string | null;
}

export interface EncounterPrescription {
  signed: boolean;
  signedAt: string | null;
  items: EncounterPrescriptionItem[];
  pdfUrl: string | null;
}

export interface EncounterAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: string;
  category: "LAB_RESULT" | "IMAGING" | "REFERRAL" | "CONSENT" | "OTHER";
  /** PENDING = presigned, bytes not yet confirmed; READY = stat() confirmed. */
  status: "PENDING" | "READY";
  /** True only when READY + a storage pointer exists. Gates the download link. */
  downloadable: boolean;
  /**
   * Bệnh nhân tự xem được tệp này qua Mini App (#18735). Mặc định false — nhân viên mở có chủ ý,
   * vì hồ sơ có thể chứa ảnh bác sĩ chưa muốn bệnh nhân tự diễn giải khi chưa được tư vấn.
   */
  patientVisible: boolean;
}

/** Presigned-PUT ticket: PUT the bytes to uploadUrl, then call completeAttachment. */
export interface PresignUploadResponse {
  attachmentId: string;
  uploadUrl: string;
  httpMethod: string;
  expiresAt: string;
}

export interface EncounterSummary {
  id: string;
  patientId: string;
  patientName: string;
  patientCode: string | null;
  patientPhone: string | null;
  patientGender: "M" | "F" | "OTHER" | "UNKNOWN" | null;
  patientAge: number | null;
  doctorId: string;
  doctorName: string;
  startedAt: string;
  completedAt: string | null;
  status: EncounterStatus;
  chiefComplaint: string | null;
  /** Comma-joined diagnosis labels (cheap summary for list pages). */
  diagnosisSummary: string | null;
  hasPrescription: boolean;
  hasVitals: boolean;
  /** Source appointment ID, if any. */
  appointmentId: string | null;
  /**
   * Ngữ cảnh ca khám lấy từ lịch hẹn nguồn (#19040). Chỉ hàng chờ khám mới có — các màn danh
   * sách không nối bảng lịch hẹn, nên ở đó chúng là `null` vì KHÔNG CÓ, chứ không phải vì thiếu.
   */
  roomId: string | null;
  roomName: string | null;
  /** Số thứ tự TRONG PHÒNG. Khi `queueScope: ROOM` mỗi phòng một dãy số riêng — đây là số bác sĩ gọi. */
  roomNo: number | null;
  /**
   * Số thứ tự tiếp đón trong ngày. Một dãy KHÁC với `roomNo`. Khác các trường trên, trường này có
   * ở MỌI danh sách (03/11/2026, tệp xuất theo mẫu SalePro); null = lịch hẹn không được cấp số.
   */
  registrationNo: number | null;
  departmentName: string | null;
  /** Giờ hẹn/tiếp đón — KHÁC `startedAt`, là lúc thật sự bắt đầu khám. */
  appointmentStartAt: string | null;
}

/** Hình thức khám (Dịch vụ/BHYT) for a visit + whether it's locked (finished). */
export interface EncounterExamMode {
  examType: "SERVICE" | "BHYT";
  bhytCardNo: string | null;
  locked: boolean;
}

export interface EncounterDetail extends EncounterSummary {
  vitals: Vitals;
  soap: SoapNote;
  diagnoses: Diagnosis[];
  prescription: EncounterPrescription;
  attachments: EncounterAttachment[];
  /**
   * Patient's own free-text description of the reason for visit, captured when
   * they booked via the mini app / web. Null when booked by staff or absent.
   */
  patientSelfDescription: string | null;
  /** Who created the booking: 'STAFF' | 'PATIENT' | 'AI_ASSISTANT' | null. */
  bookingSource: string | null;
  /**
   * Opaque token that unlocks the public (no-login) exam-result view at
   * {@code /view-order/<publicToken>/<orderCode>.html}. Null until the
   * backend has minted one (e.g. for unsigned/legacy encounters).
   */
  publicToken: string | null;
  /** Human-facing order code shown on the printout + public page. */
  orderCode: string | null;
}

export interface EncounterListFilter {
  q?: string | null;
  status?: EncounterStatus | null;
  doctorId?: string | null;
  patientId?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
}

export interface Icd10Entry {
  code: string;
  labelVi: string;
  labelEn: string;
}
